// The model showcase on the Models screen (V4-MS): the same 12 prompts on every video model. Desktop: a grid, style
// rows x model columns. Phone: pick a style, then swipe through the models. Tap a clip to play it (one at a time).
// Filters: price, fast, has sound. ?style=<id> opens a style; ?compare=<model>,<model> puts 2-4 of them side by side
// above it (the link the Director or the stage can open). Renders nothing until the showcase has clips.
import { useEffect, useMemo, useRef, useState } from "react";
import {
  cellOf, compareClips, filterModels, hasShowcase, loadModelShowcase, madeWords, NO_FILTERS, pickStyle, PRICE_FILTERS, speedOf,
  type ShowcaseCell, type ShowcaseData, type ShowcaseFilters, type ShowcaseModelInfo, type ShowcaseStyleInfo,
} from "../showcase";
import { navigate, useLocation } from "../router";
import { Icon } from "./bits";
import { ShowcaseCompare } from "./ShowcaseCompare";

/** true on screens wide enough for the full grid */
export function useWide(query = "(min-width: 900px)") {
  const get = () => (typeof window !== "undefined" && window.matchMedia ? window.matchMedia(query).matches : false);
  const [wide, setWide] = useState(get);
  useEffect(() => {
    if (!window.matchMedia) return;
    const mq = window.matchMedia(query);
    const on = () => setWide(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, [query]);
  return wide;
}

/** Loads the showcase once per mount; null while loading or when there's nothing (the section hides). */
export function useModelShowcase(): ShowcaseData | null {
  const [data, setData] = useState<ShowcaseData | null>(null);
  useEffect(() => {
    let on = true;
    void loadModelShowcase().then((d) => on && setData(d));
    return () => {
      on = false;
    };
  }, []);
  return data;
}

export function ShowcaseSection() {
  const data = useModelShowcase();
  if (!hasShowcase(data)) return null;
  return <ShowcaseGrid data={data} />;
}

export function ShowcaseGrid({ data }: { data: ShowcaseData }) {
  const { query } = useLocation();
  const wide = useWide();
  const [filters, setFilters] = useState<ShowcaseFilters>(NO_FILTERS);
  const [playing, setPlaying] = useState<string | null>(null);
  const style = pickStyle(data, query.get("style"));
  const models = useMemo(() => filterModels(data.models, filters), [data.models, filters]);
  const compare = (query.get("compare") ?? "").split(",").filter(Boolean);
  const cmp = style && compare.length >= 2 ? compareClips(data, style.id, compare) : [];

  const setStyle = (id: string) => {
    const q = new URLSearchParams(location.search);
    q.set("style", id);
    setPlaying(null);
    navigate(`${location.pathname}?${q}`, { replace: true });
  };
  const closeCompare = () => {
    const q = new URLSearchParams(location.search);
    q.delete("compare");
    navigate(`${location.pathname}${q.toString() ? `?${q}` : ""}`, { replace: true });
  };

  return (
    <section className="msc" aria-labelledby="msc-h">
      <header className="msc-head">
        <h2 id="msc-h" className="section-title">
          Same prompt, every model
        </h2>
        <p className="muted">{data.note}</p>
      </header>

      {cmp.length >= 2 && style && <ShowcaseCompare clips={cmp} title={style.name} onClose={closeCompare} />}

      <Filters f={filters} set={setFilters} shown={models.length} total={data.models.length} />

      {!models.length ? (
        <div className="msc-none">
          <p>No model matches those filters.</p>
          <button type="button" className="btn btn-quiet btn-small" onClick={() => setFilters(NO_FILTERS)}>
            Clear filters
          </button>
        </div>
      ) : wide ? (
        <Table data={data} models={models} playing={playing} play={setPlaying} />
      ) : (
        style && <Phone data={data} style={style} models={models} setStyle={setStyle} playing={playing} play={setPlaying} />
      )}
    </section>
  );
}

function Filters({ f, set, shown, total }: { f: ShowcaseFilters; set: (f: ShowcaseFilters) => void; shown: number; total: number }) {
  return (
    <div className="msc-filters" role="group" aria-label="Filter the models">
      <div className="seg" role="radiogroup" aria-label="Price per 5 seconds">
        {PRICE_FILTERS.map((p) => (
          <button key={p.id} type="button" role="radio" aria-checked={f.price === p.id} className={f.price === p.id ? "is-on" : ""} onClick={() => set({ ...f, price: p.id })}>
            {p.label}
          </button>
        ))}
      </div>
      <button type="button" className={`chip${f.fast ? " is-on" : ""}`} aria-pressed={f.fast} onClick={() => set({ ...f, fast: !f.fast })}>
        <Icon name="bolt" size={15} /> Fast
      </button>
      <button type="button" className={`chip${f.audio ? " is-on" : ""}`} aria-pressed={f.audio} onClick={() => set({ ...f, audio: !f.audio })}>
        <Icon name="volume" size={15} /> Has sound
      </button>
      <span className="msc-count muted">
        {shown === total ? `${total} models` : `${shown} of ${total} models`}
      </span>
    </div>
  );
}

/** One clip: its poster until tapped, then the video with controls (sound on). */
function Clip({ cell, model, style, playing, play, big = false }: { cell: ShowcaseCell | null; model: ShowcaseModelInfo; style: ShowcaseStyleInfo; playing: string | null; play: (k: string | null) => void; big?: boolean }) {
  if (!cell) return <div className="msc-clip is-empty">Not made yet</div>;
  const key = `${cell.styleId}|${cell.modelId}`;
  const on = playing === key;
  return on ? (
    <video className={`msc-clip${big ? " is-big" : ""}`} src={cell.videoUrl} poster={cell.posterUrl || undefined} controls autoPlay playsInline onEnded={() => play(null)} aria-label={`${model.short}, ${style.name}`} />
  ) : (
    <button type="button" className={`msc-clip${big ? " is-big" : ""}`} onClick={() => play(key)} aria-label={`Play ${model.short}: ${style.name}`}>
      {cell.posterUrl ? <img src={cell.posterUrl} alt="" loading="lazy" decoding="async" /> : null}
      <span className="msc-play" aria-hidden>
        <Icon name="play" size={big ? 22 : 16} />
      </span>
    </button>
  );
}

function Badges({ m }: { m: ShowcaseModelInfo }) {
  return (
    <span className="msc-badges">
      {speedOf(m) === "fast" && (
        <span className="tag">
          <Icon name="bolt" size={11} /> Fast
        </span>
      )}
      {m.hasAudio && (
        <span className="tag">
          <Icon name="volume" size={11} /> Sound
        </span>
      )}
    </span>
  );
}

function Table({ data, models, playing, play }: { data: ShowcaseData; models: ShowcaseModelInfo[]; playing: string | null; play: (k: string | null) => void }) {
  return (
    <div className="msc-table" role="table" aria-label="Every style on every model" style={{ ["--cols" as string]: models.length }}>
      <div className="msc-row msc-row-head" role="row">
        <span className="msc-corner" role="columnheader">
          Style
        </span>
        {models.map((m) => (
          <span key={m.id} className="msc-mhead" role="columnheader" title={m.label}>
            <b>{m.short}</b>
            <span className="msc-price">
              {m.creditsPer5s} credits <span>/ 5 s</span>
            </span>
            <Badges m={m} />
          </span>
        ))}
      </div>
      {data.styles.map((s) => (
        <div key={s.id} className="msc-row" role="row">
          <span className="msc-style" role="rowheader" title={s.watch}>
            <span className="msc-n">{String(s.n).padStart(2, "0")}</span>
            <b>{s.name}</b>
          </span>
          {models.map((m) => (
            <span key={m.id} role="cell" className="msc-cell">
              <Clip cell={cellOf(data, s.id, m.id)} model={m} style={s} playing={playing} play={play} />
            </span>
          ))}
        </div>
      ))}
    </div>
  );
}

function Phone({ data, style, models, setStyle, playing, play }: { data: ShowcaseData; style: ShowcaseStyleInfo; models: ShowcaseModelInfo[]; setStyle: (id: string) => void; playing: string | null; play: (k: string | null) => void }) {
  const track = useRef<HTMLUListElement>(null);
  const [at, setAt] = useState(0);
  useEffect(() => {
    track.current?.scrollTo({ left: 0 });
    setAt(0);
  }, [style.id, models.length]);
  const onScroll = () => {
    const el = track.current;
    const first = el?.firstElementChild as HTMLElement | null;
    if (!el || !first) return;
    const i = Math.round(el.scrollLeft / (first.offsetWidth + 12));
    if (i !== at) {
      setAt(i);
      play(null);
    }
  };
  const go = (d: number) => {
    const el = track.current;
    const first = el?.firstElementChild as HTMLElement | null;
    if (el && first) el.scrollTo({ left: Math.max(0, at + d) * (first.offsetWidth + 12), behavior: "smooth" });
  };
  return (
    <div className="msc-phone">
      <div className="msc-styles" role="tablist" aria-label="Styles">
        {data.styles.map((s) => (
          <button key={s.id} type="button" role="tab" aria-selected={s.id === style.id} className={`chip${s.id === style.id ? " is-on" : ""}`} onClick={() => setStyle(s.id)}>
            {s.name}
          </button>
        ))}
      </div>
      <p className="msc-watch">{style.watch}</p>
      <ul className="msc-track" ref={track} onScroll={onScroll} aria-label={`${style.name} on every model`}>
        {models.map((m) => {
          const c = cellOf(data, style.id, m.id);
          return (
            <li key={m.id} className="msc-card">
              <Clip cell={c} model={m} style={style} playing={playing} play={play} big />
              <div className="msc-card-text">
                <span className="msc-card-top">
                  <b>{m.short}</b>
                  <Badges m={m} />
                </span>
                <span className="msc-price">
                  {m.creditsPer5s} credits <span>per 5 s</span>
                  {m.rateSource === "unverified" && <span className="msc-est">estimate</span>}
                </span>
                {madeWords(c?.seconds ?? m.madeSeconds) && <span className="muted msc-made">{madeWords(c?.seconds ?? m.madeSeconds)}</span>}
              </div>
            </li>
          );
        })}
      </ul>
      <div className="msc-pager">
        <button type="button" className="icon-btn" onClick={() => go(-1)} disabled={at <= 0} aria-label="Previous model">
          <Icon name="back" size={18} />
        </button>
        <span className="muted" aria-live="polite">
          {Math.min(at + 1, models.length)} of {models.length}
        </span>
        <button type="button" className="icon-btn msc-next" onClick={() => go(1)} disabled={at >= models.length - 1} aria-label="Next model">
          <Icon name="back" size={18} />
        </button>
      </div>
    </div>
  );
}
